import { memo, type ReactNode } from "react";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useCodeBlock } from "./useCodeBlock";

const Block = styled("div")<{ $fill: boolean }>(({ theme, $fill }) => ({
  position: "relative",
  ...($fill && { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.action.hover,
}));

const Code = styled("pre")<{ $maxHeight?: number; $fill: boolean }>(
  ({ theme, $maxHeight, $fill }) => ({
    margin: 0,
    maxHeight: $maxHeight,
    overflowY: $maxHeight === undefined && !$fill ? undefined : "auto",
    ...($fill && { flex: 1, minHeight: 0 }),
    padding: theme.spacing(1.5, 6, 1.5, 1.5),
    fontFamily: "monospace",
    fontSize: theme.typography.body2.fontSize,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  })
);

const CopyButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(0.5),
  right: theme.spacing(0.5),
}));

export interface CodeBlockProps {
  /** The text to show and to copy, exactly as written. */
  code: string;
  /** Draws `code` with syntax colouring. It must render the text unchanged, so that what is
   *  selected on screen is what the copy button copies. Plain monospace when omitted. */
  renderCode?: (code: string) => ReactNode;
  /** Tallest the block grows, in px; longer code scrolls inside it. Unbounded when omitted. */
  maxHeight?: number;
  /** Grows into the room its container has, as a column, scrolling when the code is longer. Needs a
   *  container of a set height; `maxHeight` still caps it. */
  fill?: boolean;
  /** Label of the copy button. Default "Copy". */
  copyLabel?: string;
}

/** A read-only block of code in monospace with a copy button pinned to its corner. Pass
 *  `renderCode` to colour a language's syntax; the block itself knows no language. */
export const CodeBlock = memo(function CodeBlock({
  code,
  renderCode,
  maxHeight,
  fill = false,
  copyLabel = "Copy",
}: CodeBlockProps) {
  const { copied, copy } = useCodeBlock(code);

  return (
    <Block $fill={fill}>
      <Code $maxHeight={maxHeight} $fill={fill}>
        {renderCode ? renderCode(code) : <code>{code}</code>}
      </Code>
      <Tooltip title={copied ? "Copied" : copyLabel}>
        <CopyButton size="small" onClick={copy} aria-label={copyLabel}>
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </CopyButton>
      </Tooltip>
    </Block>
  );
});
